import { omit } from 'lodash';
import { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import debug from 'debug';

import { StoryModel } from 'types/story';
import { PARAMS } from 'common/constants';
import { StoryFacade } from 'common/utils/facades/story-facade';
import { parseURL, searchObjectToString } from 'common/utils/parse-url';
import { registerGuest, getUserData, setStory, getGame, startGame } from 'client/actions';
import { ApiEvents } from 'client/ApiEvents';
import { ClientReducerState } from 'client/reducers';

interface IMetadata {
	limits: {
		privateStories: {
			reason: 'pageviews';
			resetsAt: string;
		};
	};
}

function getMetadata(): Promise<IMetadata | null> {
	const organizationId = window.location.pathname.split('/')[1];
	return fetch(`${window.location.origin}/${organizationId}/metadata.json`).then(res => {
		if (res.ok) return res.json();
		return null;
	});
}

function isReachedPageViewsLimit(data: IMetadata) {
	const resetsAt = data.limits?.privateStories?.resetsAt;
	return !!(resetsAt && +new Date(resetsAt) > Date.now());
}

const log = debug('components:StoryFetcher');

const mapState = ({ card, user }: ClientReducerState) => ({ card, user });

const connector = connect(mapState, { registerGuest, getUserData, setStory, getGame, startGame });

type ReduxProps = ConnectedProps<typeof connector>;

type OwnProps = {
	story?: StoryModel;
	onFetch: (state: { story: StoryModel } | { isReachedPlanLimit: boolean }) => void;
	history: RouteComponentProps['history'];
	location: RouteComponentProps['location'];
};

type Props = ReduxProps & OwnProps;

type State = { story?: StoryModel };

class Story extends Component<Props, State> {
	state: State = {};

	async componentDidMount() {
		// Render story from props (admin preview)
		if (this.props.story) {
			log('render story from props', this.props.story);
			this.props.setStory(this.props.story);
			this.props.onFetch({ story: this.props.story });
			this.setState({ story: this.props.story });
			return;
		}

		let storyJSON: Omit<StoryModel, 'storyVersions'> & { storyVersion: StoryModel['storyVersions'][string] };

		try {
			if (window.location.hostname === 'localhost') {
				// Render story from static JSON (local)(usually only dev case)
				({ default: storyJSON } = await import('./story.json'));
			} else {
				// Render story from static JSON (published)

				// Check access
				const metadata = await getMetadata();
				if (metadata && isReachedPageViewsLimit(metadata)) {
					this.props.onFetch({ isReachedPlanLimit: true });
					return;
				}

				storyJSON = await fetch(`./story.json?ver=${process.env.BUILD_ID}`).then(r => r.json());
			}

			const { storyVersion: published, ...data } = storyJSON;

			const storyModel: StoryModel = {
				...data,
				storyVersions: {
					latest: published,
				},
			};
			// push story to redux store
			this.props.setStory(storyModel);
			// register
			await this.dispatchStartingEvents(storyModel);
			// provide story data to render app
			this.props.onFetch({ story: storyModel });
			// ...
			this.setState({ story: storyModel });
		} catch (e) {
			console.error('Get story.json failed.', e);
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.props.card !== prevProps.card || this.state.story !== prevState.story) {
			if (this.props.user.isOffline && this.state.story) {
				this.dispatchStartingEvents(this.state.story);
			}
		}
	}

	dispatchStartingEvents = async (story: StoryModel) => {
		log('dispatchStartingEvents', { story });

		const eventData = {
			organizationId: story.organizationId,
			storyId: story.id,
			teamId: story.teamId,
			memberId: story.createdBy,
		};

		let isUserUnregistered = !this.props.user.isRegistered;
		let isRegisteredGuest = false;
		const provider = new URLSearchParams(this.props.location.search).get(PARAMS.PROVIDER);

		if (isUserUnregistered) {
			const userData = await this.props.getUserData({ story, user: this.props.user });
			log('dispatchStartingEvents -> getUserData', { userData });

			isUserUnregistered = userData instanceof Error || !userData?.id;
			log('dispatchStartingEvents -> isUserUnregistered', isUserUnregistered);
		}

		if (isUserUnregistered) {
			const registerGuestResult = await this.props.registerGuest({ story, user: this.props.user });
			log('dispatchStartingEvents -> registerGuestResult', { registerGuestResult });
			isUserUnregistered = registerGuestResult instanceof Error;
			isRegisteredGuest = !isUserUnregistered;
		}

		const isOncePerUser = new StoryFacade(story).settings.restrictions?.oncePerUser;
		if (isOncePerUser) {
			const gameResp = await this.props.getGame(story.id);

			if (!gameResp?.game?.pageViewId) await this.props.startGame(story.id);
		}

		if (!isUserUnregistered) {
			// send register event with a exited provider
			if (provider) log('dispatchStartingEvents -> registered by provider', provider);

			ApiEvents.sendRegisterEvent(
				{
					...eventData,
					cardId: this.props.card?._id ?? '',
				},
				isRegisteredGuest
			);
		}

		if (provider) {
			// remove query
			log('dispatchStartingEvents -> remove provider query');
			const { searchObject } = parseURL(this.props.location.search);
			this.props.history.replace({ search: searchObjectToString(omit(searchObject, [PARAMS.PROVIDER])) });
		}

		ApiEvents.sendStoryViewEvent(
			{
				...eventData,
				isUserUnregistered,
			},
			isRegisteredGuest
		);
	};

	render() {
		return null;
	}
}

export default connector(Story);
